import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import ExitIntentModal from "@/components/ExitIntentModal";
import { useExitIntent } from "@/hooks/useExitIntent";
import GridActivationBridge from "@/components/GridActivationBridge";
import ReplayGreeting from "@/components/ReplayGreeting";
import { isGridInitialized, speakAs } from "@/lib/audio";
import AudioDiagnostic from "@/components/AudioDiagnostic";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  MapPin,
  Wrench,
  Shield,
  Zap,
  TrendingUp,
  MessageSquare,
  Star,
  CheckCircle2,
  Bot,
  User,
  Gauge as GaugeIcon,
  Battery,
  Fuel,
  Bike,
  Tent,
  Radio as RadioIcon,
  Flame,
  Scale,
  Music,
  AlertTriangle,
  Send,
  Activity,
  Volume2,
  VolumeX,
  Mail,
  Building2,
} from "lucide-react";

const INDUSTRY_OPTIONS = [
  "Plumbing",
  "HVAC",
  "Electrician",
  "Roofer",
  "Landscaper",
  "Auto Repair",
  "Restaurant",
  "Realtor",
  "General Contractor",
  "Campground / RV Park",
  "Other",
];

const INDUSTRY_VARIANT = {
  "Plumbing": "work_polo",
  "HVAC": "work_polo",
  "Electrician": "work_polo",
  "Roofer": "work_polo",
  "Landscaper": "work_polo",
  "Auto Repair": "work_polo",
  "General Contractor": "work_polo",
  "Restaurant": "business_casual",
  "Other": "business_casual",
  "Realtor": "suit",
  "Campground / RV Park": "campground",
};

const VARIANT_CONFIG = {
  work_polo: {
    label: "Work Polo",
    accent: "#60A5FA",
    bg: "rgba(96,165,250,0.10)",
    tagline: "For the ones who build with their hands.",
  },
  business_casual: {
    label: "Business Casual",
    accent: "#D4AF37",
    bg: "rgba(212,175,55,0.10)",
    tagline: "For the ones who host, feed and serve.",
  },
  suit: {
    label: "Suit",
    accent: "#A78BFA",
    bg: "rgba(167,139,250,0.10)",
    tagline: "For the ones who close deals over coffee.",
  },
  mc: {
    label: "Motorcycle Service Director",
    accent: "#F97316",
    bg: "rgba(249,115,22,0.10)",
    tagline: "For the ones who chase horsepower and open road.",
  },
  campground: {
    label: "Campground Concierge",
    accent: "#22C55E",
    bg: "rgba(34,197,94,0.10)",
    tagline: "For the ones who curate the great outdoors.",
  },
};

// Alfred portrait image URLs (real assets). SVG fallback used until provided.
const ALFRED_PORTRAIT_IMAGE = {
  work_polo: "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/454b5ad030a101248dd230b53f9da18d81ea957062e89a4e06e1679a50e07cac.png",
  mc: "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/a17415040e75036275681b3f809c265404c62deae626a05dc3faed4ce24477f9.png",
  campground: "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/60387ddfc29460afeb571e8f6d82cb88fb333d920987e2fcaa2d93e3d4fe2d43.png",
  business_casual: null, // TODO: supply real image URL — SVG fallback used
  suit: null,             // TODO: supply real image URL — SVG fallback used
};

// Bentley the Otter — public-facing "Guest Mode" mascot for the campground
// demo. Same Alfred spine, different face: shows owners how they can
// white-label the utility for guests without exposing "Concierge" branding.
const RANGER_BARNABY_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/c5a753ff30e72a317cf6ca3ee3efc6c9a04d9f4c85f0676f6285bedf16dd7a3b.png";
const RANGER_BARNABY_LABEL = "Bentley the Sentry";
const RANGER_BARNABY_GREETING =
  "Welcome to the river! I'm Bentley the Sentry. I can tell you about the movie tonight or help you find a snack. What can I do for you?";

// Map a `?variant=` URL param value to a canonical variant key.
const VARIANT_PARAM_ALIASES = {
  work: "work_polo",
  work_polo: "work_polo",
  trades: "work_polo",
  trade: "work_polo",
  polo: "work_polo",
  business_casual: "business_casual",
  casual: "business_casual",
  service: "business_casual",
  business: "suit",
  suit: "suit",
  professional: "suit",
  pro: "suit",
  mc: "mc",
  motorcycle: "mc",
  bike: "mc",
  moto: "mc",
  powersports: "mc",
  campground: "campground",
  camping: "campground",
  rv: "campground",
  rv_park: "campground",
  rvpark: "campground",
  outdoor: "campground",
  outdoors: "campground",
  river_bend: "campground",
  riverbend: "campground",
};

// Free-text /local write-ins that map cleanly to a canonical INDUSTRY_OPTION
// entry. Keeps the Fast-Track path snappy — user types "Auto Shop", /demo
// pre-fills the "Auto Repair" option so the Magic Mirror auto-generates.
// Lower-cased on both sides for a case-insensitive lookup.
const INDUSTRY_PARAM_ALIASES = {
  "auto shop": "Auto Repair",
  "auto": "Auto Repair",
  "auto body": "Auto Repair",
  "mechanic": "Auto Repair",
  "campground": "Campground / RV Park",
  "rv park": "Campground / RV Park",
  "rv": "Campground / RV Park",
  "motorcycle dealer": "Other",
  "insurance": "Other",
  "chiropractic": "Other",
  "chiropractor": "Other",
  "salon": "Other",
};

function resolveIndustryParam(raw) {
  if (!raw) return "";
  if (INDUSTRY_OPTIONS.includes(raw)) return raw;
  const alias = INDUSTRY_PARAM_ALIASES[raw.toLowerCase().trim()];
  return alias || "";
}

// When only a `?variant=` param is provided (no specific industry), show a
// generic tier label instead of picking one industry as a stand-in.
const GENERIC_INDUSTRY_LABEL = {
  work_polo: "Trade & Automotive",
  business_casual: "Hospitality & Service",
  suit: "Professional",
  mc: "Motorcycle & Powersports",
  campground: "Campground & RV Park",
};
const GENERIC_INDUSTRY_LABELS = new Set(Object.values(GENERIC_INDUSTRY_LABEL));

// Short verbatim noun for the L2 tier when industry is generic.
const VARIANT_L2_NOUN = {
  work_polo: "service call",
  business_casual: "reservation",
  suit: "listing lead",
  mc: "service ticket",
  campground: "site booking",
};

function isGenericIndustry(label) {
  return GENERIC_INDUSTRY_LABELS.has(label);
}

// The industry noun used in "…marketing operations stack for X operators…".
function operatorNoun(industry) {
  return (industry || "local").toLowerCase();
}

// The inquiry noun used in "Capture every X" on the L2 tier.
function inquiryNoun(industry, variant) {
  if (isGenericIndustry(industry)) {
    return VARIANT_L2_NOUN[variant] || "inquiry";
  }
  return `${industry.toLowerCase()} inquiry`;
}

export default function Demo() {
  const [searchParams] = useSearchParams();
  const [ownerName, setOwnerName] = useState("");
  const [shopName, setShopName] = useState("");
  const [contact, setContact] = useState("");
  const [city, setCity] = useState("");
  const [industry, setIndustry] = useState("");
  const [generated, setGenerated] = useState(null);
  const [validationHint, setValidationHint] = useState("");
  // Partial-lead capture — the moment the user hits Generate we log them,
  // and arm the exit-intent bribe modal for salvage.
  const [leadId, setLeadId] = useState(null);
  const [showExitModal, setShowExitModal] = useState(false);

  // URL prefill — allows John to text a personalized link:
  //   /demo?name=Buckeye+Auto&city=Delaware&variant=work
  //   /demo?name=Mobile+Cycle+Works&city=Marion             (MC auto-detected)
  //   /demo?name=Mobile+Cycle+Works&city=Marion&variant=mc  (explicit)
  useEffect(() => {
    const nameParam = (searchParams.get("name") || "").trim();
    const cityParam = (searchParams.get("city") || "").trim();
    const variantParam = (searchParams.get("variant") || "").trim().toLowerCase();
    const industryRaw = (searchParams.get("industry") || "").trim();
    // Resolve free-text industry (e.g. "Auto Shop") to a canonical
    // INDUSTRY_OPTIONS entry so the Magic Mirror can auto-generate.
    const industryParam = resolveIndustryParam(industryRaw);

    if (!nameParam && !cityParam && !variantParam && !industryRaw) return;

    const mcAutoDetect = isMotorcycleContext(nameParam, industryRaw);
    const campgroundAutoDetect = isCampgroundContext(nameParam, industryRaw);

    // Final variant resolution — vertical keyword auto-detect always wins over
    // an explicit ?variant= so a "River Bend Campground" shop typed into the
    // URL never gets served the wrong Alfred.
    //   1. Campground keyword match → campground
    //   2. MC keyword match         → mc
    //   3. explicit ?variant=       → alias-mapped variant
    //   4. explicit ?industry=      → INDUSTRY_VARIANT map
    //   5. fallback                 → business_casual
    let finalVariant = "";
    if (campgroundAutoDetect) {
      finalVariant = "campground";
    } else if (mcAutoDetect) {
      finalVariant = "mc";
    } else if (variantParam && VARIANT_PARAM_ALIASES[variantParam]) {
      finalVariant = VARIANT_PARAM_ALIASES[variantParam];
    } else if (industryParam && INDUSTRY_OPTIONS.includes(industryParam)) {
      finalVariant = INDUSTRY_VARIANT[industryParam] || "business_casual";
    }

    // Final industry label — explicit ?industry= wins, otherwise use the
    // generic label attached to the resolved variant.
    let finalIndustry = "";
    if (industryParam && INDUSTRY_OPTIONS.includes(industryParam)) {
      finalIndustry = industryParam;
    } else if (finalVariant) {
      finalIndustry = GENERIC_INDUSTRY_LABEL[finalVariant] || "";
    }

    if (nameParam) setShopName(nameParam);
    if (cityParam) setCity(cityParam);
    if (finalIndustry) setIndustry(finalIndustry);

    // Auto-generate if we have name + city AND any variant signal
    // (explicit variant, explicit industry, or keyword auto-detect).
    const hasVariantSignal =
      !!variantParam || !!industryParam || mcAutoDetect || campgroundAutoDetect;

    if (nameParam && cityParam && hasVariantSignal && finalVariant) {
      setGenerated({
        shopName: nameParam,
        city: cityParam,
        industry: finalIndustry || GENERIC_INDUSTRY_LABEL[finalVariant] || "",
        variant: finalVariant,
      });
    }
    // Only run on first mount
  }, []);

  const canGenerate = !!(
    ownerName.trim() &&
    shopName.trim() &&
    contact.trim() &&
    city.trim() &&
    industry
  );

  // Clear inline validation hint the moment the user fills the missing field.
  useEffect(() => {
    if (validationHint && canGenerate) setValidationHint("");
  }, [validationHint, canGenerate]);

  // Fire a partial (contact-only) lead POST to the CRM so cold /demo traffic
  // never disappears. Non-blocking — if it fails, generation still proceeds.
  const captureLead = async (variant) => {
    const contactIsEmail = /@/.test(contact);
    try {
      const { data } = await api.post("/local/lead", {
        owner_name: ownerName.trim(),
        business_name: shopName.trim(),
        email: contactIsEmail ? contact.trim() : "",
        phone: contactIsEmail ? "" : contact.trim(),
        city: city.trim(),
        industry,
        source: `demo-${variant || "form"}`,
      });
      setLeadId(data.lead_id);
    } catch (err) {
      console.warn("demo lead capture failed", err?.response?.data || err);
    }
  };

  const handleGenerate = (e) => {
    e?.preventDefault();
    // Inline validation — never leave the user staring at a "dead" button.
    const missing = [];
    if (!ownerName.trim()) missing.push("your name");
    if (!shopName.trim()) missing.push("shop name");
    if (!contact.trim()) missing.push("email or phone");
    if (!city.trim()) missing.push("city");
    if (!industry) missing.push("industry");
    if (missing.length) {
      setValidationHint(`Add your ${missing.join(", ")} to begin the assessment.`);
      return;
    }
    setValidationHint("");
    // Vertical keyword auto-detect still wins on manual form submit — e.g.,
    // user typed "River Bend Campground" and picked "Other" from the dropdown.
    const campgroundAutoDetect = isCampgroundContext(shopName, industry);
    const mcAutoDetect = isMotorcycleContext(shopName, industry);
    const finalVariant = campgroundAutoDetect
      ? "campground"
      : mcAutoDetect
        ? "mc"
        : INDUSTRY_VARIANT[industry] || "business_casual";
    setGenerated({
      shopName: shopName.trim(),
      city: city.trim(),
      industry,
      variant: finalVariant,
    });
    // Background lead capture — arms exit-intent modal.
    captureLead(finalVariant);
    // Scroll to top so preview lands cleanly
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => {
    setGenerated(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Arm exit-intent bribe modal ONLY after we have a captured lead.
  useExitIntent(() => setShowExitModal(true), !!leadId);

  const exitModal = (
    <ExitIntentModal
      open={showExitModal}
      onClose={() => setShowExitModal(false)}
      contact={contact}
      businessName={shopName}
    />
  );

  if (generated) {
    return (
      <>
        <PilotPagePreview data={generated} onReset={reset} />
        {/* Grid Activation Bridge — sits below the preview so cold traffic
            sees readiness + briefing + WhatsApp CTA before they leave. */}
        <div className="relative z-10 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 pb-14 -mt-2 bg-[#060B14]">
          <GridActivationBridge
            niche={generated.variant === "campground" ? "campground" : generated.industry}
            businessName={generated.shopName}
            contact={contact}
            leadId={leadId}
          />
        </div>
        {exitModal}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#060B14] text-white relative overflow-hidden flex flex-col" data-testid="demo-page">
      {/* Ambient backdrop (same as Landing) */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 30%, rgba(212,175,55,0.10) 0%, transparent 55%), radial-gradient(ellipse at 50% 90%, rgba(96,165,250,0.06) 0%, transparent 60%), linear-gradient(180deg, #060B14 0%, #0A1122 55%, #060B14 100%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(212,175,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.5) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between gap-3">
          <Link to="/" data-testid="demo-logo-link">
            <Logo />
          </Link>
          <div className="flex items-center gap-3 shrink-0">
            <ReplayGreeting character="alfred" variant="compact" label="Replay Alfred" />
            <div className="flex items-center gap-2 text-[10px] hud-label">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] light-pulse glow-yellow" />
              Magic Mirror · Live
            </div>
          </div>
        </div>
      </header>

      {/* Console */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-12">
        <div className="w-full max-w-2xl">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <div className="hud-line w-10" />
            <span className="hud-label">Pilot Seat Generator</span>
            <div className="hud-line w-10" />
          </div>

          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-center"
            data-testid="demo-headline"
          >
            See <span className="text-gold italic">your business</span>
            <br />
            in the Alfred stack.
          </h1>
          <p className="mt-5 text-sm sm:text-base text-slate-400 text-center max-w-xl mx-auto">
            Enter your details. In 4 seconds we&apos;ll spin up a preview of the branded Pilot Seat
            we would launch for you — no sign-up, no email.
          </p>

          {/* Form Card */}
          <form
            onSubmit={handleGenerate}
            className="mt-10 glass-strong rounded-sm p-6 sm:p-8 relative"
            data-testid="demo-form"
          >
            <div
              className="absolute inset-x-0 top-0 h-0.5"
              style={{ background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }}
            />

            <div className="space-y-5">
              <Field
                label="Your Name"
                icon={<User size={12} />}
                htmlFor="demo-owner-name"
              >
                <input
                  id="demo-owner-name"
                  type="text"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  placeholder="Jane Wexler"
                  data-testid="demo-input-owner-name"
                  className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-base focus:outline-none focus:border-gold min-h-[48px]"
                  maxLength={80}
                  autoComplete="name"
                />
              </Field>

              <Field
                label="Shop / Business Name"
                icon={<Building2 size={12} />}
                htmlFor="demo-shop-name"
              >
                <input
                  id="demo-shop-name"
                  type="text"
                  value={shopName}
                  onChange={(e) => setShopName(e.target.value)}
                  placeholder="Waldo Tire & Automotive"
                  data-testid="demo-input-shop-name"
                  className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-base focus:outline-none focus:border-gold min-h-[48px]"
                  maxLength={80}
                />
              </Field>

              <Field
                label="Best Email or Phone"
                icon={<Mail size={12} />}
                htmlFor="demo-contact"
              >
                <input
                  id="demo-contact"
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="jane@wexlerauto.com  or  (614) 555-0142"
                  data-testid="demo-input-contact"
                  className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-base focus:outline-none focus:border-gold min-h-[48px]"
                  maxLength={80}
                  autoComplete="email"
                />
              </Field>

              <Field
                label="City"
                icon={<MapPin size={12} />}
                htmlFor="demo-city"
              >
                <input
                  id="demo-city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Marion, OH"
                  data-testid="demo-input-city"
                  className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-base focus:outline-none focus:border-gold"
                  maxLength={60}
                />
              </Field>

              <Field
                label="Industry"
                icon={<Wrench size={12} />}
                htmlFor="demo-industry"
              >
                <div className="relative">
                  <select
                    id="demo-industry"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    data-testid="demo-input-industry"
                    className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-base focus:outline-none focus:border-gold appearance-none cursor-pointer"
                  >
                    <option value="">— Choose your trade —</option>
                    {INDUSTRY_OPTIONS.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                  <ArrowRight
                    size={14}
                    className="absolute right-4 top-1/2 -translate-y-1/2 rotate-90 text-slate-500 pointer-events-none"
                  />
                </div>
              </Field>
            </div>

            <button
              type="submit"
              onClick={handleGenerate}
              data-testid="demo-generate-button"
              aria-disabled={!canGenerate}
              className="btn-gold w-full mt-6 sm:mt-8 py-4 rounded-sm text-sm sm:text-base font-display uppercase tracking-[0.14em] sm:tracking-widest inline-flex items-center justify-center gap-2 sm:gap-3 transition-opacity min-h-[52px] whitespace-normal text-center"
              style={
                canGenerate
                  ? { boxShadow: "0 0 32px rgba(212,175,55,0.4)", opacity: 1 }
                  : { opacity: 0.55 }
              }
            >
              <Sparkles size={16} /> Begin AI Utility Assessment
            </button>

            {validationHint && (
              <div
                className="mt-3 text-center text-[11px] uppercase tracking-[0.2em] text-[#F59E0B]"
                data-testid="demo-validation-hint"
                role="alert"
              >
                {validationHint}
              </div>
            )}

            <div className="mt-5 text-center text-[10px] text-slate-500 uppercase tracking-wider font-mono-aviation">
              Instant preview · nothing is saved · walk away anytime
            </div>
          </form>
        </div>
      </main>

      <footer className="relative z-10 pb-10 pt-6">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 flex justify-center">
          <Link
            to="/"
            data-testid="demo-back-home"
            className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-slate-500 hover:text-gold transition-colors"
          >
            <Shield size={11} className="opacity-70 group-hover:opacity-100" />
            <span>Return to Master Switchboard</span>
            <ArrowRight
              size={11}
              className="opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"
            />
          </Link>
        </div>
      </footer>
      {exitModal}
      <AudioDiagnostic />
    </div>
  );
}

function Field({ label, icon, htmlFor, children }) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2"
      >
        <span className="text-gold">{icon}</span>
        {label}
      </label>
      {children}
    </div>
  );
}

// Detect motorcycle/two-wheel context from the lead's shopName or industry so
// the AI Service Writer sample swaps to MC-specific probes. Case-insensitive
// substring match — "Mobile Cycle Works" → true.
function isMotorcycleContext(shopName, industry) {
  const hay = `${shopName || ""} ${industry || ""}`.toLowerCase();
  return (
    hay.includes("motorcycle") ||
    hay.includes("cycle") ||
    hay.includes("bike")
  );
}

// Detect campground / RV park context so the AI Service Writer swaps to
// campground-specific probes. "River Bend Family Campground" → true.
function isCampgroundContext(shopName, industry) {
  const hay = `${shopName || ""} ${industry || ""}`.toLowerCase();
  return (
    hay.includes("campground") ||
    hay.includes("rv park") ||
    hay.includes("rv resort") ||
    hay.includes("camping") ||
    hay.includes("river bend")
  );
}


/* --------------------------------------------------------------------------
   Generated Pilot Page preview — transient, in-memory only.
   -------------------------------------------------------------------------- */

// Auto-play voice greeting for the River Bend / Campground preview.
// Uses the browser's Web Speech API (SpeechSynthesis) — no backend / no API key.
// The user has already interacted (clicked "Generate"), so autoplay is
// permitted by Chrome/Safari's audio-gesture policy.
const BENTLEY_GREETING_TEXT =
  "Welcome to the Last 50 Mile Grid at River Bend Family Campground! I'm Bentley, your Adventure Captain on the AI utility team. I'm here to turn every trip into a legendary experience.";

function BentleyVoiceGreeting({ accent, shopName }) {
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  // Personalise if the shop name isn't the demo default.
  const line = useMemo(() => {
    if (!shopName || /river bend/i.test(shopName)) return BENTLEY_GREETING_TEXT;
    return BENTLEY_GREETING_TEXT.replace(
      "River Bend Family Campground",
      shopName
    );
  }, [shopName]);

  const speak = () => {
    // Route through the centralized audio module so Bentley always
    // gets a strict male voice (Web Speech default is female on most
    // systems, which was mis-gendering him).
    try {
      setSpeaking(true);
      // Rough "done" timer — SpeechSynthesis inside speakAs doesn't
      // expose onend here since it's fire-and-forget.
      const approxMs = Math.min(9000, Math.max(2000, line.length * 55));
      setTimeout(() => setSpeaking(false), approxMs);
      speakAs("bentley", line);
    } catch (_) {
      setSpeaking(false);
    }
  };

  // Auto-play once on mount. Voices may load async in Chrome, so we wait one
  // "voiceschanged" tick if needed.
  useEffect(() => {
    if (muted) return;
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) {
      const handler = () => {
        speak();
        window.speechSynthesis.removeEventListener("voiceschanged", handler);
      };
      window.speechSynthesis.addEventListener("voiceschanged", handler);
      // Fallback in case voiceschanged never fires.
      const t = setTimeout(speak, 600);
      return () => {
        clearTimeout(t);
        window.speechSynthesis.removeEventListener("voiceschanged", handler);
        window.speechSynthesis.cancel();
      };
    }
    const t = setTimeout(speak, 350);
    return () => {
      clearTimeout(t);
      window.speechSynthesis.cancel();
    };
  }, []);

  const toggle = () => {
    if (muted) {
      setMuted(false);
      speak();
    } else {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      setMuted(true);
      setSpeaking(false);
    }
  };

  const replay = () => {
    setMuted(false);
    speak();
  };

  return (
    <div
      className="inline-flex items-center gap-2 pl-2.5 pr-1.5 py-1 rounded-full border"
      style={{
        borderColor: `${accent}55`,
        background: "rgba(6,11,20,0.75)",
      }}
      data-testid="bentley-voice-greeting"
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{
          backgroundColor: speaking ? accent : "#64748B",
          boxShadow: speaking ? `0 0 8px ${accent}` : "none",
          transition: "all 0.2s",
        }}
      />
      <span
        className="text-[10px] uppercase tracking-[0.24em]"
        style={{ color: accent }}
      >
        {speaking ? "Bentley speaking…" : muted ? "Voice muted" : "Bentley voice"}
      </span>
      <button
        onClick={replay}
        className="p-1 rounded-full hover:bg-white/10 transition-colors"
        title="Replay greeting"
        data-testid="bentley-voice-replay"
      >
        <Volume2 size={12} style={{ color: accent }} />
      </button>
      <button
        onClick={toggle}
        className="p-1 rounded-full hover:bg-white/10 transition-colors"
        title={muted ? "Unmute" : "Mute"}
        data-testid="bentley-voice-toggle"
      >
        {muted ? (
          <VolumeX size={12} style={{ color: "#94A3B8" }} />
        ) : (
          <Volume2 size={12} style={{ color: "#475569" }} />
        )}
      </button>
    </div>
  );
}

function PilotPagePreview({ data, onReset }) {
  const cfg = VARIANT_CONFIG[data.variant];
  const isMotorcycle = isMotorcycleContext(data.shopName, data.industry);
  const isCampground = data.variant === "campground" || isCampgroundContext(data.shopName, data.industry);
  // Mascot toggle — only surfaces on the campground demo. "office" keeps the
  // internal-facing Concierge Alfred; "guest" swaps in Bentley the Otter for
  // the white-label guest-facing preview.
  const [mascot, setMascot] = useState("office");
  const initial = useMemo(
    () => data.shopName.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase(),
    [data.shopName]
  );  return (
    <div
      className="min-h-screen bg-[#060B14] text-white relative overflow-hidden flex flex-col"
      data-testid="pilot-preview-page"
    >
      {/* Preview Ribbon */}
      <div
        className="relative z-30 border-b border-gold/30 bg-[#0A1122]/95 backdrop-blur-sm"
        data-testid="pilot-preview-ribbon"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-11 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-gold">
            <Sparkles size={11} />
            <span>Live Preview · {cfg.label} Variant</span>
          </div>
          <button
            onClick={onReset}
            data-testid="pilot-preview-reset"
            className="text-[10px] uppercase tracking-[0.25em] text-slate-400 hover:text-white inline-flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft size={11} /> Try another business
          </button>
        </div>
      </div>

      {/* Backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at 50% 30%, ${cfg.accent}22 0%, transparent 55%), radial-gradient(ellipse at 50% 90%, rgba(212,175,55,0.06) 0%, transparent 60%), linear-gradient(180deg, #060B14 0%, #0A1122 55%, #060B14 100%)`,
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 min-h-16 py-3 flex flex-wrap items-center justify-between gap-3">
          {isCampground ? <SentryWordmark accent={cfg.accent} /> : <Logo />}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-end">
            {isCampground && (
              <BentleyVoiceGreeting accent={cfg.accent} shopName={data.shopName} />
            )}
            <div className="flex items-center gap-2 text-[10px] hud-label">
              <span
                className="w-1.5 h-1.5 rounded-full light-pulse shrink-0"
                style={{ backgroundColor: cfg.accent }}
              />
              <span className="truncate max-w-[200px]">
                {isCampground
                  ? `Sentry Online · ${data.shopName}`
                  : `Seat Reserved for ${data.shopName}`}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main className="relative z-10 flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-8 sm:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          {/* Alfred Portrait + optional Mascot Toggle (campground only) */}
          <div className="lg:col-span-2 flex flex-col items-center lg:items-start gap-4">
            <AlfredPortrait
              variant={data.variant}
              mascot={isCampground ? mascot : "office"}
              testId="pilot-portrait"
            />
            {isCampground && (
              <MascotToggle
                mascot={mascot}
                setMascot={setMascot}
                accent={cfg.accent}
              />
            )}
          </div>

          {/* Copy */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-5">
              <div className="hud-line w-8" />
              <span className="hud-label">Your Pilot Seat</span>
            </div>
            <h1
              className="font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05]"
              data-testid="pilot-headline"
            >
              <span className="block text-white">{data.shopName}</span>
              <span
                className="block mt-2 text-2xl sm:text-3xl"
                style={{ color: isCampground ? cfg.accent : "#94A3B8" }}
              >
                × {isCampground ? "Alfred · Digital Sentry" : "Wingman Alfred"}
              </span>
            </h1>
            <p
              className="mt-6 text-base text-slate-300 leading-relaxed"
              data-testid="pilot-subheadline"
            >
              {isCampground ? (
                <>
                  A <span className="text-white">24-hour digital front desk</span>{" "}
                  for {data.shopName} in{" "}
                  <span className="text-white">{data.city}</span>. Never a missed
                  guest, never an overnight fire.
                </>
              ) : (
                <>
                  A dedicated marketing operations stack for{" "}
                  <span className="text-white">{operatorNoun(data.industry)}</span> operators in{" "}
                  <span className="text-white">{data.city}</span>. Fully activated in 30 days,
                  backed by the 80% Success Guarantee.
                </>
              )}
            </p>

            <div className="mt-4 text-[11px] text-slate-500 italic font-display">
              {cfg.tagline}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <button
                type="button"
                data-testid="pilot-cta-primary"
                className="btn-gold px-8 py-4 rounded-sm text-sm font-display uppercase tracking-widest inline-flex items-center gap-2"
                style={{ boxShadow: `0 0 28px ${cfg.accent}55` }}
                onClick={() =>
                  window.scrollTo({
                    top: document.getElementById("pilot-tiers")?.offsetTop || 0,
                    behavior: "smooth",
                  })
                }
              >
                Reserve Your Seat <ArrowRight size={14} />
              </button>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-aviation">
                30-day operational activation · Central Ohio pilot cohort
              </div>
            </div>

            {/* Local hook badge */}
            <div className="mt-8 inline-flex items-center gap-3 px-4 py-2.5 rounded-sm border border-white/10 bg-white/[0.03]">
              <div className="w-9 h-9 rounded-sm border flex items-center justify-center font-display text-sm shrink-0"
                   style={{ borderColor: `${cfg.accent}66`, color: cfg.accent }}>
                {initial}
              </div>
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-slate-500">Assigned Coordinator</div>
                <div className="text-sm text-white font-display">John Delawder · Lead Architect</div>
              </div>
            </div>
          </div>
        </div>

        {/* Sentry Dashboard — shown only for campground variant (portfolio demo:
            campground / legal / entertainment sectors, all backed by Alfred). */}
        {isCampground && (
          <SentryPanel
            accent={cfg.accent}
            shopName={data.shopName}
            city={data.city}
            mascot={mascot}
          />
        )}

        {/* Utility Tiers */}
        <section id="pilot-tiers" className="mt-20 scroll-mt-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="hud-line w-8" />
            <span className="hud-label">The Four-Tier Utility Stack</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pilot-tiers">
            <UtilityTier
              tier="L1"
              icon={<Star size={16} />}
              title="Reputation"
              sub={`5-star flywheel for ${data.city}`}
              accent="#34D399"
            />
            <UtilityTier
              tier="L2"
              icon={<TrendingUp size={16} />}
              title="Lead Net"
              sub={`Capture every ${inquiryNoun(data.industry, data.variant)}`}
              accent="#60A5FA"
            />
            <UtilityTier
              tier="L3"
              icon={<Zap size={16} />}
              title="Content Engine"
              sub="Local SEO + weekly assets"
              accent="#D4AF37"
            />
            <UtilityTier
              tier="L4"
              icon={<MessageSquare size={16} />}
              title="Voice"
              sub="Missed-call recovery within 60 sec"
              accent="#A78BFA"
            />
          </div>
        </section>

        {/* AI Service Writer Preview — sample intake + guaranteed probes */}
        <section className="mt-16" data-testid="pilot-service-writer">
          <div className="flex items-center gap-3 mb-6">
            <div className="hud-line w-8" />
            <span className="hud-label">AI Service Writer · Sample Intake</span>
          </div>

          <div className="glass-strong rounded-sm p-6 sm:p-8 relative overflow-hidden">
            <div
              className="absolute inset-x-0 top-0 h-0.5"
              style={{ background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }}
            />

            <p className="text-sm text-slate-400 leading-relaxed mb-6 max-w-2xl">
              Here&apos;s what your customers would see when they message{" "}
              <span className="text-white">{data.shopName}</span>. Your AI {isCampground ? "Concierge" : "Service Writer"} opens
              every conversation the same way — and it&apos;s trained to <span className="text-gold">always</span> ask
              two diagnostic probes before it hands off to your team.
            </p>

            {/* Chat mock */}
            <div className="space-y-4">
              {/* AI opener */}
              <ChatBubble side="ai" testId="sw-prompt-opener">
                <div className="hud-label text-[9px] mb-2">Opening Intake</div>
                <div className="text-white text-base leading-relaxed">
                  {isCampground ? (
                    <>
                      Welcome to {data.shopName}. Check-in Status: are you a nightly camper,
                      a seasonal resident, or visiting for an event?
                    </>
                  ) : isMotorcycle ? (
                    <>
                      Describe what&apos;s happening. Are you noticing changes in handling,
                      strange vibrations, or specific warning lights?
                    </>
                  ) : (
                    <>
                      Describe what&apos;s happening. Include any dashboard warning lights or
                      specific computer messages you&apos;re seeing.
                    </>
                  )}
                </div>
              </ChatBubble>

              {/* Simulated customer reply — swapped to match vertical */}
              <ChatBubble side="user" testId="sw-customer-reply">
                <div className="hud-label text-[9px] mb-2 text-slate-500">Sample Customer Reply</div>
                <div className="text-slate-300 text-sm leading-relaxed italic">
                  {isCampground ? (
                    <>
                      &ldquo;Here for the weekend car show — need to check in, and my
                      propane tank is bone dry. Site 12 if that helps.&rdquo;
                    </>
                  ) : isMotorcycle ? (
                    <>
                      &ldquo;Front fork feels squishy and I&apos;m getting a knocking sound
                      when I let off the throttle. No warning lights.&rdquo;
                    </>
                  ) : (
                    <>
                      &ldquo;Check-engine light came on this morning and the car shakes at idle.
                      Says P0300 on my scanner.&rdquo;
                    </>
                  )}
                </div>
              </ChatBubble>

              {/* Probe A — guaranteed */}
              {isCampground ? (
                <ChatBubble
                  side="ai"
                  accent="#22C55E"
                  icon={<Flame size={14} />}
                  testId="sw-probe-support-type"
                >
                  <div className="hud-label text-[9px] mb-2" style={{ color: "#22C55E" }}>
                    Guaranteed Probe · Support Type
                  </div>
                  <div className="text-white text-base leading-relaxed">
                    Support Type: Site Maintenance, Propane Refill, Snack Shack, or General Question?
                  </div>
                </ChatBubble>
              ) : isMotorcycle ? (
                <ChatBubble
                  side="ai"
                  accent="#60A5FA"
                  icon={<Bike size={14} />}
                  testId="sw-probe-ridable"
                >
                  <div className="hud-label text-[9px] mb-2" style={{ color: "#60A5FA" }}>
                    Guaranteed Probe · Ridability
                  </div>
                  <div className="text-white text-base leading-relaxed">
                    On a scale of 1&ndash;10, is the bike safely ridable, or do you need to
                    arrange a trailer/tow?
                  </div>
                </ChatBubble>
              ) : (
                <ChatBubble
                  side="ai"
                  accent="#60A5FA"
                  icon={<GaugeIcon size={14} />}
                  testId="sw-probe-commute"
                >
                  <div className="hud-label text-[9px] mb-2" style={{ color: "#60A5FA" }}>
                    Guaranteed Probe · Impact
                  </div>
                  <div className="text-white text-base leading-relaxed">
                    On a scale of 1&ndash;10, how much is this affecting your daily commute?
                  </div>
                </ChatBubble>
              )}

              {/* Probe B — guaranteed */}
              {isCampground ? (
                <ChatBubble
                  side="ai"
                  accent="#A78BFA"
                  icon={<RadioIcon size={14} />}
                  testId="sw-probe-sms-loop"
                >
                  <div className="hud-label text-[9px] mb-2" style={{ color: "#A78BFA" }}>
                    Guaranteed Probe · Real-Time Intel
                  </div>
                  <div className="text-white text-base leading-relaxed">
                    Would you like to join our Weekend Agenda SMS loop for live event alerts?{" "}
                    <span className="text-slate-400 text-sm">
                      (Opt-in captures the phone for future weekend reactivation blasts.)
                    </span>
                  </div>
                </ChatBubble>
              ) : isMotorcycle ? (
                <ChatBubble
                  side="ai"
                  accent="#A78BFA"
                  icon={<Fuel size={14} />}
                  testId="sw-probe-fuel-system"
                >
                  <div className="hud-label text-[9px] mb-2" style={{ color: "#A78BFA" }}>
                    Guaranteed Probe · Fuel System
                  </div>
                  <div className="text-white text-base leading-relaxed">
                    Is this a carbureted or fuel-injected model?{" "}
                    <span className="text-slate-400 text-sm">
                      (Critical for service writers to know before scheduling.)
                    </span>
                  </div>
                </ChatBubble>
              ) : (
                <ChatBubble
                  side="ai"
                  accent="#A78BFA"
                  icon={<Battery size={14} />}
                  testId="sw-probe-hybrid-ev"
                >
                  <div className="hud-label text-[9px] mb-2" style={{ color: "#A78BFA" }}>
                    Guaranteed Probe · Safety Prep
                  </div>
                  <div className="text-white text-base leading-relaxed">
                    Is this a Hybrid or EV?{" "}
                    <span className="text-slate-400 text-sm">
                      (This ensures we have the right Certified Technician and safety equipment ready.)
                    </span>
                  </div>
                </ChatBubble>
              )}
            </div>

            {/* Footer note */}
            <div className="mt-8 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-mono-aviation">
                <CheckCircle2 size={12} className="text-gold" />
                Two diagnostic probes fire on every intake · guaranteed
              </div>
              <div
                className="text-[10px] font-mono-aviation"
                style={{ color: (isCampground || isMotorcycle) ? "#D4AF37" : "#64748B" }}
                data-testid="sw-playbook-label"
              >
                Sample shown is the {isCampground ? "campground" : isMotorcycle ? "motorcycle" : "automotive"} playbook.
              </div>
            </div>
          </div>
        </section>

        {/* Guarantee strip */}
        <section className="mt-16 glass-strong rounded-sm p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div
            className="w-14 h-14 rounded-sm border flex items-center justify-center shrink-0"
            style={{ borderColor: "#D4AF37", color: "#D4AF37" }}
          >
            <CheckCircle2 size={22} />
          </div>
          <div className="flex-1">
            <div className="hud-label mb-1">The 80% Success Guarantee</div>
            <p className="text-sm text-slate-300 leading-relaxed">
              If {data.shopName} does not see a measurable operational lift within 90 days, we
              refund the pilot fee and de-brief you on what we learned. No lock-in. No games.
            </p>
          </div>
        </section>
      </main>

      {/* Powered By footer */}
      <footer className="relative z-10 pb-10 pt-6 border-t border-white/5 mt-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
            {isCampground ? (
              <>
                Powered by{" "}
                <span style={{ color: cfg.accent }}>Alfred · The Digital Sentry</span>
              </>
            ) : (
              <>
                Powered by <span className="text-gold">Wingman Alfred</span>
              </>
            )}
          </div>
          <button
            onClick={onReset}
            data-testid="pilot-footer-reset"
            className="text-[10px] uppercase tracking-[0.28em] text-slate-500 hover:text-gold transition-colors inline-flex items-center gap-1.5"
          >
            <ArrowLeft size={10} /> Generate another
          </button>
        </div>
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Sentry Wordmark & Multi-Sector Portfolio Panel                     *
 *  Renders only for campground demo. Owner can toggle across their    *
 *  three businesses (campground / legal / entertainment) to see the   *
 *  same Alfred spine drive each one.                                  *
 * ------------------------------------------------------------------ */

function SentryWordmark({ accent }) {
  return (
    <div className="flex items-center gap-2.5" data-testid="sentry-wordmark">
      <div
        className="w-8 h-8 rounded-sm border flex items-center justify-center"
        style={{ borderColor: `${accent}88`, background: `${accent}12` }}
      >
        <Shield size={14} style={{ color: accent }} />
      </div>
      <div className="leading-tight">
        <div className="font-display text-[13px] tracking-widest text-white">
          ALFRED
        </div>
        <div
          className="text-[8px] uppercase tracking-[0.32em]"
          style={{ color: accent }}
        >
          Digital Sentry
        </div>
      </div>
    </div>
  );
}

// Alfred · Digital Sentry — campground-only configuration. Portfolio toggle
// (Legal / Entertainment sectors) has been stripped; this demo is now
// laser-focused on the campground vertical.
const CAMPGROUND_CFG = {
  id: "campground",
  accent: "#22C55E",
  persona: "The Digital Sentry",
  tagline: "24-hour digital front desk",
  activityLabel: "Live Activity Pulse",
  gauge: { label: "Front Desk Noise Reduction", value: 85 },
  events: [
    "10:15 PM · Maintenance Ticket · Site 22 flagged for slow drip",
    "10:47 PM · Pool Hours inquiry answered · guest at Site 14",
    "11:02 PM · Bologna Express arrival broadcast pushed to 42 sites",
    "11:18 PM · Propane refill queued for morning · Site 07",
    "11:36 PM · Weekend Agenda SMS opt-in · +6 subscribers",
    "12:04 AM · Hog Roast Saturday reminder · 84 confirmed",
    "12:29 AM · Quiet-hours soft-warned · Site 31",
  ],
  emergencyLabel: "Simulate Midnight Emergency",
  emergency: {
    title: "12:47 AM · POWER OUTAGE · Loop C (Sites 20–35)",
    body: "Alfred paged the on-call maintenance lead, auto-texted all 16 affected sites with candles/flashlight info + generator ETA, and paused all outbound guest broadcasts until service was restored.",
    chips: ["Maintenance paged", "16 sites notified", "Broadcasts paused"],
  },
  replies: [
    {
      match: ["pool", "swim"],
      text: "Pool is open 8 AM – 10 PM daily. Lifeguard 12 – 8. Kid-swim ends 8 PM sharp. Water shoes required in the shower house — want me to text you the map?",
    },
    {
      match: ["food truck", "food", "bologna", "eat"],
      text: "Bologna Express rolls in Friday 5 – 9 PM by the pavilion. Sausage sandwiches, wood-fired pizza, house lemonade. Cash & card. I already told them to expect 40+ tonight.",
    },
    {
      match: ["movie", "10 pm", "amphitheater", "cinema"],
      text: "Tonight's 10 PM outdoor movie is Top Gun: Maverick at the amphitheater. Bring a chair — I'll SMS the trailer link to your Weekend Agenda loop if you're opted in.",
    },
    {
      match: ["hog roast", "saturday", "event"],
      text: "Saturday Hog Roast: 6 PM main pavilion. $18/plate, kids under 8 free. 84 confirmed. Mac, slaw, cornbread. Buckeye String Band starts 7:30.",
    },
    {
      match: ["check in", "check-in", "checkin", "arrive"],
      text: "Check-in is 3 PM. Late arrival after 10 PM? I'll drop your gate code + site map in a text — no need to wake the front desk.",
    },
    {
      match: ["wifi", "wi-fi"],
      text: "Wi-Fi: RiverBend-Guest. Password rotates weekly — tonight's is on your welcome text. Strongest signal within 60 ft of the shower houses & pavilion.",
    },
    {
      match: ["maintenance", "leak", "broken", "site"],
      text: "Logged as a maintenance ticket against your site number. On-call lead is paged if it's urgent, otherwise it's first thing 7 AM. You'll get a text when it's cleared.",
    },
    {
      match: ["quiet", "noise"],
      text: "Quiet hours are 10 PM – 8 AM. If a neighbor is over the line, tell me the site number and I'll send a soft warning + loop the ranger if it repeats.",
    },
  ],
  defaultReply:
    "I'll route that to the front desk in the morning — the on-call team is asleep, but nothing will slip. Want me to text you the answer at 8 AM?",
  sampleQuestions: ["Pool hours?", "Is there a food truck?", "What's at 10 PM?"],
};

function SentryPanel({ shopName, city, mascot = "office" }) {
  const cfg = CAMPGROUND_CFG;
  const isGuestMascot = mascot === "guest";
  const personaLabel = isGuestMascot ? "Bentley the Sentry" : `Alfred · ${cfg.persona}`;
  const speakerName = isGuestMascot ? "Bentley" : "Alfred";
  return (
    <section
      className="mt-12 rounded-sm border p-5 sm:p-7"
      style={{
        borderColor: `${cfg.accent}44`,
        background:
          `linear-gradient(180deg, ${cfg.accent}0A 0%, transparent 60%), rgba(6,11,20,0.55)`,
        boxShadow: `0 0 32px -18px ${cfg.accent}66`,
      }}
      data-testid="sentry-panel"
    >
      {/* Panel header — sector toggle removed, laser-focused on campground. */}
      <div className="mb-5">
        <div
          className="text-[9px] uppercase tracking-[0.32em]"
          style={{ color: cfg.accent }}
          data-testid="sentry-persona-label"
        >
          {personaLabel}
        </div>
        <div className="font-display text-lg tracking-tight text-white mt-1">
          {shopName}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {cfg.tagline} · {city}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ActivityPulse cfg={cfg} />
        <InteractionSim
          cfg={cfg}
          speakerName={speakerName}
          guestGreeting={isGuestMascot ? RANGER_BARNABY_GREETING : null}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
        <EmergencySimulator cfg={cfg} />
        <InsulationGauge cfg={cfg} />
      </div>
    </section>
  );
}

function ActivityPulse({ cfg }) {
  const items = useMemo(() => [...cfg.events, ...cfg.events], [cfg.events]);
  return (
    <div
      className="rounded-sm border p-4 h-64 flex flex-col"
      style={{
        borderColor: `${cfg.accent}33`,
        background: "rgba(6,11,20,0.65)",
      }}
      data-testid={`sentry-activity-${cfg.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity size={12} style={{ color: cfg.accent }} />
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">
            {cfg.activityLabel}
          </div>
        </div>
        <div
          className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em]"
          style={{ color: cfg.accent }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full light-pulse"
            style={{ backgroundColor: cfg.accent }}
          />
          Live
        </div>
      </div>

      <div className="flex-1 overflow-hidden relative">
        <div
          className="absolute inset-0"
          style={{
            maskImage:
              "linear-gradient(180deg, transparent 0%, black 15%, black 85%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(180deg, transparent 0%, black 15%, black 85%, transparent 100%)",
          }}
        >
          <div
            className="sentry-marquee font-mono-aviation text-[11px] leading-relaxed"
            style={{ color: "#CBD5E1" }}
          >
            {items.map((line, i) => (
              <div key={i} className="py-1.5 flex items-start gap-2">
                <span
                  className="mt-1 w-1 h-1 rounded-full shrink-0"
                  style={{ backgroundColor: cfg.accent }}
                />
                <span>{line}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function InteractionSim({ cfg, speakerName = "Alfred", guestGreeting = null }) {
  const [conversation, setConversation] = useState([]);
  const [input, setInput] = useState("");

  const resolveReply = (question) => {
    const q = question.trim().toLowerCase();
    const hit = cfg.replies.find((r) => r.match.some((kw) => q.includes(kw)));
    return hit ? hit.text : cfg.defaultReply;
  };

  const send = (raw) => {
    const question = (raw ?? input).trim();
    if (!question) return;
    const reply = resolveReply(question);
    setConversation((prev) => [
      ...prev,
      { role: "user", text: question },
      { role: "alfred", text: reply },
    ]);
    setInput("");
  };

  // Reset conversation when sector or mascot greeting changes so a Guest-Mode
  // switch mid-conversation cleanly re-primes the greeting.
  useEffect(() => {
    setConversation([]);
    setInput("");
  }, [cfg.id, guestGreeting]);

  return (
    <div
      className="rounded-sm border p-4 h-64 flex flex-col"
      style={{
        borderColor: `${cfg.accent}33`,
        background: "rgba(6,11,20,0.65)",
      }}
      data-testid={`sentry-sim-${cfg.id}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare size={12} style={{ color: cfg.accent }} />
        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">
          Live Interaction Sim
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto space-y-2 pr-1"
        data-testid={`sentry-sim-log-${cfg.id}`}
      >
        {conversation.length === 0 ? (
          <div className="text-[11px] leading-relaxed space-y-2">
            {guestGreeting && (
              <div
                className="rounded-sm px-2.5 py-2 mr-6"
                style={{
                  background: `${cfg.accent}12`,
                  border: `1px solid ${cfg.accent}33`,
                  color: "#F1F5F9",
                }}
                data-testid="sentry-sim-greeting"
              >
                <div
                  className="text-[8px] uppercase tracking-[0.28em] mb-1"
                  style={{ color: cfg.accent }}
                >
                  {speakerName}
                </div>
                <div className="text-[12px]">{guestGreeting}</div>
              </div>
            )}
            <div className="text-slate-500 italic">
              Ask {speakerName} anything. Try:{" "}
              {cfg.sampleQuestions.map((q, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => send(q)}
                  data-testid={`sentry-sim-sample-${cfg.id}-${i}`}
                  className="inline-block mr-1.5 mt-1 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-[0.2em] hover:text-white transition-colors"
                  style={{
                    border: `1px solid ${cfg.accent}55`,
                    color: cfg.accent,
                    background: `${cfg.accent}08`,
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          conversation.map((m, i) => (
            <div
              key={i}
              className={`text-[12px] leading-relaxed rounded-sm px-2.5 py-1.5 ${
                m.role === "user" ? "ml-6" : "mr-6"
              }`}
              style={{
                background:
                  m.role === "user" ? "rgba(255,255,255,0.05)" : `${cfg.accent}12`,
                border:
                  m.role === "user"
                    ? "1px solid rgba(255,255,255,0.08)"
                    : `1px solid ${cfg.accent}33`,
                color: m.role === "user" ? "#CBD5E1" : "#F1F5F9",
              }}
            >
              <div
                className="text-[8px] uppercase tracking-[0.28em] mb-0.5"
                style={{ color: m.role === "user" ? "#64748B" : cfg.accent }}
              >
                {m.role === "user" ? "Guest" : speakerName}
              </div>
              {m.text}
            </div>
          ))
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="mt-3 flex items-center gap-2"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a question…"
          data-testid={`sentry-sim-input-${cfg.id}`}
          className="flex-1 bg-[#060B14] border rounded-sm px-3 py-2 text-[13px] text-white focus:outline-none"
          style={{ borderColor: "rgba(255,255,255,0.08)" }}
          onFocus={(e) => (e.target.style.borderColor = cfg.accent)}
          onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.08)")}
        />
        <button
          type="submit"
          data-testid={`sentry-sim-send-${cfg.id}`}
          className="h-9 w-9 rounded-sm inline-flex items-center justify-center"
          style={{ background: cfg.accent, color: "#060B14" }}
          aria-label="Send"
        >
          <Send size={13} />
        </button>
      </form>
    </div>
  );
}

function EmergencySimulator({ cfg }) {
  const [fired, setFired] = useState(false);
  // Reset on sector change so each tab starts un-fired.
  useEffect(() => {
    setFired(false);
  }, [cfg.id]);
  return (
    <div
      className="rounded-sm border p-4"
      style={{
        borderColor: fired ? `${cfg.accent}66` : "rgba(239,68,68,0.35)",
        background: fired ? `${cfg.accent}0A` : "rgba(120,20,20,0.10)",
        transition: "background 0.3s, border-color 0.3s",
      }}
      data-testid={`sentry-emergency-${cfg.id}`}
    >
      {!fired ? (
        <button
          type="button"
          onClick={() => setFired(true)}
          data-testid={`sentry-emergency-trigger-${cfg.id}`}
          className="w-full py-3 rounded-sm inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.26em] transition-colors"
          style={{
            background: "rgba(239,68,68,0.10)",
            border: "1px solid rgba(239,68,68,0.45)",
            color: "#FCA5A5",
          }}
        >
          <AlertTriangle size={13} /> {cfg.emergencyLabel}
        </button>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div
              className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em]"
              style={{ color: cfg.accent }}
            >
              <AlertTriangle size={12} /> Sentry Responded
            </div>
            <button
              type="button"
              onClick={() => setFired(false)}
              data-testid={`sentry-emergency-reset-${cfg.id}`}
              className="text-[9px] uppercase tracking-[0.24em] text-slate-500 hover:text-white transition-colors"
            >
              Reset
            </button>
          </div>
          <div className="font-mono-aviation text-[12px] text-white mb-2">
            {cfg.emergency.title}
          </div>
          <p className="text-[12px] text-slate-300 leading-relaxed mb-3">
            {cfg.emergency.body}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {cfg.emergency.chips.map((c) => (
              <span
                key={c}
                className="px-2 py-1 rounded-sm text-[9px] uppercase tracking-[0.22em]"
                style={{
                  background: `${cfg.accent}12`,
                  border: `1px solid ${cfg.accent}55`,
                  color: cfg.accent,
                }}
              >
                <CheckCircle2 size={10} className="inline mr-1 -mt-0.5" />
                {c}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InsulationGauge({ cfg }) {
  const { label, value } = cfg.gauge;
  return (
    <div
      className="rounded-sm border p-4"
      style={{
        borderColor: `${cfg.accent}33`,
        background: "rgba(6,11,20,0.65)",
      }}
      data-testid={`sentry-gauge-${cfg.id}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Volume2 size={12} style={{ color: cfg.accent }} />
        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">
          Insulation Gauge
        </div>
      </div>
      <div className="text-[11px] text-slate-300 leading-tight mb-2">{label}</div>
      <div className="flex items-baseline gap-2">
        <div
          className="font-display text-4xl font-light leading-none"
          style={{ color: cfg.accent }}
        >
          {value}
        </div>
        <div className="text-sm text-slate-500">%</div>
      </div>
      <div className="mt-3 h-1.5 rounded-full overflow-hidden bg-white/5">
        <div
          className="h-full rounded-full"
          style={{
            width: `${value}%`,
            background: `linear-gradient(90deg, ${cfg.accent}55, ${cfg.accent})`,
          }}
        />
      </div>
    </div>
  );
}


function ChatBubble({ side, accent = "#D4AF37", icon, children, testId }) {
  const isAi = side === "ai";
  const iconEl = icon
    ? icon
    : isAi
    ? <Bot size={14} />
    : <User size={14} />;
  return (
    <div
      className={`flex items-start gap-3 ${isAi ? "" : "flex-row-reverse"}`}
      data-testid={testId}
    >
      <div
        className="w-9 h-9 rounded-sm border flex items-center justify-center shrink-0"
        style={{
          borderColor: isAi ? `${accent}55` : "rgba(148,163,184,0.35)",
          color: isAi ? accent : "#94A3B8",
          backgroundColor: isAi ? `${accent}12` : "rgba(148,163,184,0.06)",
        }}
      >
        {iconEl}
      </div>
      <div
        className={`flex-1 max-w-2xl rounded-sm p-4 border ${isAi ? "" : "text-right"}`}
        style={{
          borderColor: isAi ? `${accent}30` : "rgba(148,163,184,0.15)",
          backgroundColor: isAi ? `${accent}0A` : "rgba(148,163,184,0.04)",
        }}
      >
        <div className={isAi ? "" : "text-right"}>{children}</div>
      </div>
    </div>
  );
}

function UtilityTier({ tier, icon, title, sub, accent }) {
  return (
    <div
      className="glass rounded-sm p-5 border-l-2 relative overflow-hidden"
      style={{ borderLeftColor: accent }}
      data-testid={`pilot-tier-${tier.toLowerCase()}`}
    >
      <div className="flex items-center gap-2 mb-3" style={{ color: accent }}>
        {icon}
        <span className="hud-label" style={{ color: accent }}>{tier}</span>
      </div>
      <div className="font-display text-lg text-white mb-1">{title}</div>
      <div className="text-[11px] text-slate-400 leading-relaxed">{sub}</div>
    </div>
  );
}

/* Placeholder Alfred portrait — SVG-based silhouette until John provides
   the real 3-variant photo assets. Distinct look per variant via accent
   color and outfit accent (polo collar / open button-up / suit lapel). */
function AlfredPortrait({ variant, mascot = "office", testId }) {
  const cfg = VARIANT_CONFIG[variant];
  const isGuestMascot = mascot === "guest";
  const imageUrl = isGuestMascot ? RANGER_BARNABY_IMAGE : ALFRED_PORTRAIT_IMAGE[variant];
  const badgeLabel = isGuestMascot ? RANGER_BARNABY_LABEL : cfg.label;
  const captionName = isGuestMascot ? "Bentley" : "Alfred";
  return (
    <div
      className="relative rounded-sm border overflow-hidden"
      style={{
        width: 280,
        height: 340,
        borderColor: `${cfg.accent}55`,
        background: `linear-gradient(180deg, ${cfg.bg} 0%, rgba(6,11,20,0.8) 100%)`,
      }}
      data-testid={testId}
    >
      {/* Corner ticks */}
      <span className="absolute top-2 left-2 w-3 h-3 border-t border-l z-20" style={{ borderColor: cfg.accent }} />
      <span className="absolute top-2 right-2 w-3 h-3 border-t border-r z-20" style={{ borderColor: cfg.accent }} />
      <span className="absolute bottom-2 left-2 w-3 h-3 border-b border-l z-20" style={{ borderColor: cfg.accent }} />
      <span className="absolute bottom-2 right-2 w-3 h-3 border-b border-r z-20" style={{ borderColor: cfg.accent }} />

      {imageUrl ? (
        <>
          {/* Real Alfred photo */}
          <img
            src={imageUrl}
            alt={`${captionName} · ${badgeLabel}`}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`alfred-photo-${isGuestMascot ? "bentley" : variant}`}
            loading="lazy"
          />
          {/* Subtle bottom vignette so the label chip stays legible */}
          <div
            className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, transparent 0%, rgba(6,11,20,0.75) 100%)",
            }}
          />
        </>
      ) : (
        <>
          {/* Halo */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle at 50% 30%, ${cfg.accent}30 0%, transparent 55%)`,
            }}
          />

          {/* SVG silhouette fallback */}
          <svg viewBox="0 0 200 240" className="absolute inset-0 w-full h-full">
            <circle cx="100" cy="82" r="36" fill={cfg.accent} opacity="0.85" />
            <circle cx="100" cy="82" r="36" fill="url(#face-shine)" />
            <defs>
              <radialGradient id="face-shine" cx="0.4" cy="0.3" r="0.7">
                <stop offset="0%" stopColor="#F8FAFC" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#000000" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect x="90" y="115" width="20" height="18" fill={cfg.accent} opacity="0.7" />
            <path
              d="M 30 240 Q 30 155, 100 148 T 170 240 Z"
              fill={cfg.accent}
              opacity="0.55"
            />
            {variant === "business_casual" && (
              <>
                <path d="M 80 145 L 100 190 L 120 145 Z" fill="#060B14" opacity="0.85" />
                <line x1="100" y1="155" x2="100" y2="215" stroke="#060B14" strokeWidth="1.5" opacity="0.4" />
                <circle cx="100" cy="175" r="2" fill={cfg.accent} />
                <circle cx="100" cy="200" r="2" fill={cfg.accent} />
              </>
            )}
            {variant === "suit" && (
              <>
                <path d="M 68 155 L 100 220 L 100 175 Z" fill="#060B14" opacity="0.9" />
                <path d="M 132 155 L 100 220 L 100 175 Z" fill="#060B14" opacity="0.9" />
                <path d="M 94 152 L 106 152 L 108 158 L 100 240 L 92 158 Z" fill={cfg.accent} opacity="0.9" />
                <path d="M 96 154 L 104 154 L 105 158 L 100 168 L 95 158 Z" fill="#060B14" opacity="0.5" />
              </>
            )}
          </svg>
        </>
      )}

      {/* Label chip */}
      <div className="absolute left-3 right-3 bottom-3 z-20 flex items-center justify-between">
        <div className="text-[9px] uppercase tracking-[0.25em] text-slate-300 font-mono-aviation">
          {captionName}
        </div>
        <div
          className="px-2 py-0.5 rounded-sm text-[9px] uppercase tracking-widest border"
          style={{
            color: cfg.accent,
            borderColor: `${cfg.accent}66`,
            backgroundColor: "rgba(6,11,20,0.75)",
          }}
        >
          {badgeLabel}
        </div>
      </div>
    </div>
  );
}

function MascotToggle({ mascot, setMascot, accent }) {
  const options = [
    { key: "office", label: "Office Mode", icon: "⚙️" },
    { key: "guest", label: "Guest Mode", icon: "🌲" },
  ];
  return (
    <div
      className="inline-flex p-1 rounded-sm border"
      style={{
        borderColor: "rgba(255,255,255,0.08)",
        background: "rgba(255,255,255,0.02)",
      }}
      data-testid="mascot-toggle"
      role="tablist"
      aria-label="Mascot mode"
    >
      {options.map((o) => {
        const active = o.key === mascot;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => setMascot(o.key)}
            data-testid={`mascot-tab-${o.key}`}
            role="tab"
            aria-selected={active}
            className="px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-[0.24em] transition-colors inline-flex items-center gap-1.5"
            style={{
              color: active ? "#060B14" : "#94A3B8",
              background: active ? accent : "transparent",
              fontWeight: active ? 700 : 400,
            }}
          >
            <span aria-hidden="true">{o.icon}</span>
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
